import { isEmpty } from "lodash/fp";
import { shallow } from "zustand/shallow";

import { Divider } from "@mui/material";
import styled from "@mui/material/styles/styled";

import type { ForYouPage } from "@app/analytics/Pulse/types";

import Dropdown from "@rui/Dropdown";
import DropdownMenuItem from "@rui/Dropdown/DropdownMenuItem";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import Stack from "@rui/Stack/Stack";

import { StyledListSubheader } from "./styles";
import usePulseStore from "../stores/pulse";

const SelectorDropdown = styled(Dropdown)(({ theme }) => ({
  minWidth: "200px",
  "& .MuiSelect-select": {
    color: theme.palette.shades.neutral[700],
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.shades.neutral[100],
  },
}));

const PulseDateSelector = ({ page }: { page: ForYouPage }) => {
  const { options, selectedRange, setSelectedRange } = usePulseStore(
    (state) => ({
      options: state[page].dropdownOptions,
      selectedRange: state[page].selectedRange,
      setSelectedRange: state.setSelectedRange,
    }),
    shallow
  );

  if (options == null || isEmpty(options)) {
    return (
      <SelectorDropdown
        value={selectedRange.value}
        name="pulse-date-range"
        selectedItem={selectedRange}
        placeholder={options == null ? "Loading..." : "-"}
      />
    );
  }

  const selectedItem = getSelectedFromOptions(options, selectedRange.value);
  const [mostRecentOption, ...pastOptions] = options;

  return (
    <SelectorDropdown
      value={selectedRange.value}
      name="pulse-date-range"
      selectedItem={selectedItem}
    >
      <StyledListSubheader>Most Recent</StyledListSubheader>
      <DropdownMenuItem
        key={mostRecentOption.value}
        selected={mostRecentOption.value === selectedRange.value}
        value={mostRecentOption.value}
        onClick={() => setSelectedRange(page, mostRecentOption)}
        label={mostRecentOption.label}
      />
      {!isEmpty(pastOptions) && (
        <Stack>
          <Divider sx={{ marginX: 1.5, marginTop: 1 }} />
          <StyledListSubheader>Past (Mon - Sun)</StyledListSubheader>
        </Stack>
      )}
      {pastOptions.map((option) => (
        <DropdownMenuItem
          key={option.value}
          {...option}
          selected={option.value === selectedRange.value}
          onClick={() => setSelectedRange(page, option)}
        />
      ))}
    </SelectorDropdown>
  );
};

export default PulseDateSelector;
